import {
  DrawFeature,
  DrawSetting,
  DrawSettings,
  getDistributedValue,
  MapExtend,
  MAX_CIRCLE_RADIUS,
  MAX_STROKE_WIDTH,
  MIN_CIRCLE_RADIUS,
  MIN_STROKE_WIDTH,
} from '@agissc/ktag-map';
import { BBox, Feature, Geometry } from 'geojson';
import GeoJson from 'ol/format/GeoJSON';
import { bboxPolygon, booleanIntersects } from '@turf/turf';
import styleQuery from '../../../assets/geostyle/search-result-style.json';
import styleQueryAttributeSearchResults from '../../../assets/geostyle/attributeSearch-result-style.json';
import styleQueryAttributeSelectedResults from '../../../assets/geostyle/attributeSearch-selected-style.json';
import locationRed from '../../../assets/icons/location-red.svg?raw';
import convertSvgToPngBase64 from '../../../utils/convertSvgToPngBase64';

const meterToPx = 3779.5275590551;

const alphaTo255 = (alpha: number) => Math.round(alpha * 2.55);

const hexToRgba = (hex: any, alpha: any) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b, alpha];
};

const getFillColor = (drawSetting: DrawSettings, opacity?: number) => {
  const customOpacity =
    opacity ??
    (drawSetting[DrawSetting.FilledOut] ? Number(drawSetting[DrawSetting.Opacity]) : 0);

  return hexToRgba(drawSetting[DrawSetting.Color], alphaTo255(customOpacity));
};

const getStrokeColor = (color: string, opacity: number = 100) => {
  return hexToRgba(color, alphaTo255(Number(opacity)));
};

const getStrokeWidth = (drawSetting: DrawSettings) =>
  getDistributedValue(
    drawSetting[DrawSetting.StrokeWidth],
    MIN_STROKE_WIDTH,
    MAX_STROKE_WIDTH,
  );

const createGeometry = (type: string, coordinates: any) => ({
  spatialReference: { wkid: 2056 },
  __type: type,
  ...coordinates,
});

const polygonToGraphic = (
  coordinates: any,
  drawSetting: DrawSettings,
  strokeColor?: string,
  opacity?: number,
) => ({
  geometry: createGeometry('polygon', { rings: coordinates }),
  symbol: {
    color: getFillColor(drawSetting, opacity),
    outline: {
      color: getStrokeColor(
        strokeColor ?? drawSetting[DrawSetting.Color],
        strokeColor ? 100 : drawSetting[DrawSetting.Opacity],
      ),
      width: getStrokeWidth(drawSetting),
      type: 'esriSLS',
      style: 'esriSLSSolid',
    },
    style: 'esriSFSSolid',
    type: 'esriSFS',
    __type: 'simplefillsymbol',
  },
});

const lineToGraphic = (coordinates: any, drawSetting: DrawSettings) => {
  const isSinglePart = typeof coordinates[0][0] === 'number';
  const paths = isSinglePart ? [coordinates] : coordinates;
  return {
    geometry: createGeometry('polyline', { paths }),
    symbol: {
      color: getStrokeColor(
        drawSetting[DrawSetting.Color],
        drawSetting[DrawSetting.Opacity],
      ),
      width: getStrokeWidth(drawSetting),
      style: 'esriSLSSolid',
      type: 'esriSLS',
      __type: 'simplelinesymbol',
    },
  };
};

const pointToGraphic = (center: any, drawSetting: DrawSettings) => ({
  geometry: createGeometry('point', { x: center[0], y: center[1] }),
  symbol: {
    color: getStrokeColor(
      drawSetting[DrawSetting.Color],
      drawSetting[DrawSetting.Opacity],
    ),
    size: getDistributedValue(
      drawSetting[DrawSetting.Radius] * 2,
      MIN_CIRCLE_RADIUS,
      MAX_CIRCLE_RADIUS,
    ),
    style: 'esriSMSCircle',
    type: 'esriSMS',
    __type: 'simplemarkersymbol',
  },
});

const pngToGraphic = (
  center: any,
  imageData: string,
  width: number,
  xoffset: number,
  yoffset: number,
) => ({
  geometry: createGeometry('point', { x: center[0], y: center[1] }),
  symbol: {
    imageData,
    xoffset,
    yoffset,
    width,
    height: width,
    type: 'esriPMS',
    __type: 'picturemarkersymbol',
  },
});

const circleToGraphic = (center: any, drawSetting: DrawSettings, size: number) => ({
  geometry: createGeometry('point', { x: center[0], y: center[1] }),
  symbol: {
    color: getFillColor(drawSetting),
    size,
    outline: {
      color: getStrokeColor(
        drawSetting[DrawSetting.Color],
        drawSetting[DrawSetting.Opacity],
      ),
      width: getStrokeWidth(drawSetting),
      type: 'esriSLS',
      style: 'esriSLSSolid',
    },
    style: 'esriSMSCircle',
    type: 'esriSMS',
    __type: 'simplemarkersymbol',
  },
});

const labelToGraphic = (center: any, label: string, angle: number) => ({
  geometry: createGeometry('point', { x: center[0], y: center[1] }),
  symbol: {
    __type: 'textsymbol',
    color: [255, 255, 255, 255],
    type: 'esriTS',
    haloSize: 3,
    haloColor: [0, 0, 0, 255],
    horizontalAlignment: 'center',
    angle,
    text: label,
    rotated: false,
    kerning: true,
    font: {
      size: 12,
      style: 'normal',
      weight: 'bold',
      family: 'sans-serif',
    },
    // backgroundcolor: [45, 45, 52, 255],
  },
});

const textToGraphic = (
  center: any,
  text: string,
  angle: number,
  drawSetting: DrawSettings,
) => ({
  geometry: createGeometry('point', { x: center[0], y: center[1] }),
  symbol: {
    __type: 'textsymbol',
    color: getStrokeColor(drawSetting[DrawSetting.Color]),
    type: 'esriTS',
    horizontalAlignment: 'center',
    angle,
    text,
    rotated: false,
    kerning: true,
    font: {
      size: drawSetting[DrawSetting.FontSize],
      style: 'normal',
      weight: 'bold',
      family: 'sans-serif',
    },
  },
});

export const getGraphic = (
  feature: Feature<Geometry>,
  props: any,
  angle: number,
  printScale: number,
) => {
  const { type: geometryType } = feature.geometry;
  const { radius, vertices, text, settings, type, center } = props;

  switch (geometryType) {
    case 'Polygon':
      return polygonToGraphic(feature.geometry.coordinates, settings);
    case 'MultiPolygon':
      return polygonToGraphic(feature.geometry.coordinates[0], settings);

    case 'LineString':
      return type === 'arrow'
        ? polygonToGraphic(
            [vertices],
            settings,
            settings[DrawSetting.Color],
            settings[DrawSetting.Opacity],
          )
        : lineToGraphic(feature.geometry.coordinates, settings);

    case 'Point':
      return type === 'text'
        ? textToGraphic(feature.geometry.coordinates, text, angle, settings)
        : pointToGraphic(feature.geometry.coordinates, settings);

    default:
  }

  if (type === 'circle')
    return circleToGraphic(center, settings, (radius * 2 * meterToPx) / printScale);

  return null;
};

export const mapToGraphics = (
  drawFeatures: DrawFeature[],
  printScale: number,
  angle: number,
) => {
  if (!drawFeatures.length) return [];

  const newAngle = angle * -1;

  return drawFeatures.flatMap((feature) => {
    const geoJsonFeature = new GeoJson().readFeatures(feature)[0];
    const props = geoJsonFeature.getProperties();
    const { label, labelCoordinates, type, displayTooltip } = props;

    const graphics: any[] = [];
    const graphic = getGraphic(feature, props, newAngle, printScale);

    if (graphic) {
      graphics.push(graphic);

      if (displayTooltip && type !== 'dot' && type !== 'text') {
        graphics.push(labelToGraphic(labelCoordinates, label, newAngle));
      }
    }
    return graphics;
  });
};

export const searchToGraphic = async (feature: any) => {
  if (feature.geometry.type === 'Point') {
    const style = styleQuery.rules[0].symbolizers[1];
    const imageData = await convertSvgToPngBase64(locationRed);

    return pngToGraphic(
      feature.geometry.coordinates,
      imageData,
      style.size!,
      style.offset![0],
      style.offset![1],
    );
  }

  if (feature.geometry.type === 'MultiPolygon' || feature.geometry.type === 'Polygon') {
    const style = styleQuery.rules[0].symbolizers[0];

    const settings: DrawSettings = {
      [DrawSetting.Color]: style.color!,
      [DrawSetting.DisplayTooltip]: false,
      [DrawSetting.FilledOut]: true,
      [DrawSetting.FontSize]: 0,
      [DrawSetting.Opacity]: style.fillOpacity! * 100,
      [DrawSetting.Radius]: 0,
      [DrawSetting.StrokeWidth]: style.outlineWidth!,
    };

    const coodinates =
      feature.geometry.type === 'MultiPolygon'
        ? feature.geometry.coordinates[0]
        : feature.geometry.coordinates;
    return polygonToGraphic(coodinates, settings, style.outlineColor);
  }

  return null;
};

export const attributeSearchToGraphic = (
  attributeSearchFeatures: any,
  selectedFeatureId: number | undefined,
  printExtent: MapExtend,
) => {
  if (!attributeSearchFeatures) return [];

  const style = styleQueryAttributeSearchResults.rules[0].symbolizers[1];
  const styleSelected = styleQueryAttributeSelectedResults.rules[0].symbolizers[1];

  const bBox: BBox = [
    printExtent.xMin,
    printExtent.yMin,
    printExtent.xMax,
    printExtent.yMax,
  ];

  return attributeSearchFeatures.flatMap((feature: Feature<Geometry>) => {
    if (booleanIntersects(feature, bboxPolygon(bBox))) {
      const settings: DrawSettings = {
        [DrawSetting.Color]:
          feature.id === selectedFeatureId ? styleSelected.color! : style.color!,
        [DrawSetting.DisplayTooltip]: false,
        [DrawSetting.FilledOut]: true,
        [DrawSetting.FontSize]: 0,
        [DrawSetting.Opacity]: 80,
        [DrawSetting.Radius]: 10,
        [DrawSetting.StrokeWidth]: 35,
      };
      const { type: geometryType } = feature.geometry;

      if (geometryType === 'Polygon') {
        return [polygonToGraphic(feature.geometry.coordinates, settings)];
      }

      if (geometryType === 'MultiPolygon') {
        return [polygonToGraphic(feature.geometry.coordinates[0], settings)];
      }

      if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
        return [lineToGraphic(feature.geometry.coordinates, settings)];
      }

      if (geometryType === 'Point') {
        settings[DrawSetting.FilledOut] = false;
        return [pointToGraphic(feature.geometry.coordinates, settings)];
      }
    }
    return [];
  });
};
